// @ts-nocheck
import { useAuth } from '@/contexts/AuthContext'

/**
 * Permission modules and actions matching TeamEditDrawer / UserEditDrawer / RoleManager.
 * Format: { module: { action: boolean } }
 * 
 * Computes EFFECTIVE permissions from:
 *   Role permissions → Team permissions → User permissions (additive merge)
 *
 * Only SUPER_ADMIN has full unrestricted access.
 * ADMIN (org admin) and ADVISOR roles respect effective_permissions set by the admin portal.
 */

export function usePermissions() {
  const { user } = useAuth()

  if (!user) {
    return {
      can: () => false,
      canAny: () => false,
      canAll: () => false,
      isAdmin: false,
      isSuperAdmin: false,
      isOrgAdmin: false,
      effectivePermissions: {} as Record<string, Record<string, boolean>>,
    }
  }

  const role = (user.role || '').toLowerCase()
  const isSuperAdmin = role === 'super_admin' || user.is_super_admin === true
  const isOrgAdmin = role === 'admin'
  // Only SUPER_ADMIN bypasses permission checks.
  const isAdmin = isSuperAdmin

  // Precomputed effective permissions from the backend (Role + Teams + User merged)
  const effectivePermissions: Record<string, Record<string, boolean>> = 
    user.effective_permissions || {}

  /**
   * Check if the user has a specific permission.
   * @param module - e.g. 'clients', 'tasks', 'settings'
   * @param action - e.g. 'view', 'create', 'edit', 'delete'
   */
  function can(module: string, action: string): boolean {
    if (isSuperAdmin) return true
    return effectivePermissions[module]?.[action] === true
  }

  /**
   * Check if the user has ANY of the given permissions.
   */
  function canAny(...checks: [string, string][]): boolean {
    if (isSuperAdmin) return true
    return checks.some(([mod, act]) => effectivePermissions[mod]?.[act] === true)
  }

  /**
   * Check if the user has ALL of the given permissions.
   */
  function canAll(...checks: [string, string][]): boolean {
    if (isSuperAdmin) return true
    return checks.every(([mod, act]) => effectivePermissions[mod]?.[act] === true)
  }

  return {
    can,
    canAny,
    canAll,
    isAdmin,
    isSuperAdmin,
    isOrgAdmin,
    effectivePermissions,
  }
}
